import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Button,
  GDSStyleObject,
  HeadingText,
  ParagraphText,
  Popover,
  StackLayout,
  UtilityText,
  ProgressBar,
  focusOutlineOuter,
  type GDSHeadingLevel,
} from '@leagueplatform/genesis-core';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import {
  MasonryDeeplinkAction,
  MasonryDriverAction,
} from '../../types/masonry-driver-node-actions';
import { MasonryProgressBarCardRendererType } from './masonry-progress-bar-card-renderer.type';

const overrideGenesisFocusVisibleStyles = {
  '&[class*="GDS"]:focus': {
    outline: 'none',
  },
  '&[class*="GDS"]:focus-visible': {
    ...focusOutlineOuter,
  },
};

interface MasonryProgressBarCardRendererProps
  extends MasonryProgressBarCardRendererType {
  onClick?: MasonryDriverAction;
  linkRefState?: React.RefObject<HTMLAnchorElement>;
  isDismissible?: boolean;
  css?: GDSStyleObject;
}

const getTooltipHeadingLevel = (headingLevel: GDSHeadingLevel) => {
  const level = parseInt(headingLevel, 10) + 1;
  if (level > 6) {
    return '6';
  }
  return level.toString() as GDSHeadingLevel;
};

export const MasonryProgressBarCardContentRenderer = ({
  eyebrow,
  heading,
  currentText,
  progress,
  suffixText,
  footerText,
  onClick,
  linkRefState,
  headingLevel: headingLevelProp,
  emphasizeCurrentValue = false,
  emphasizeFooterText = false,
  isDismissible,
  tooltipContent,
  tooltipTitle,
  tooltipLabel,
  tooltipIcon,
  css,
}: MasonryProgressBarCardRendererProps) => {
  const { formatMessage } = useIntl();
  const invokeAction = useInvokeAction();

  const onClickDeeplinkAction = onClick as MasonryDeeplinkAction;
  const icon = tooltipIcon || 'interfaceQuestionCircle';
  const headingLevel = headingLevelProp || '3';
  const tooltipHeadingLevel = React.useMemo(
    () => getTooltipHeadingLevel(headingLevel),
    [headingLevel],
  );

  return (
    <StackLayout
      orientation="vertical"
      spacing="$quarter"
      css={{
        margin: `${
          isDismissible
            ? '$three $oneAndHalf $oneAndHalf $oneAndHalf'
            : '$oneAndHalf'
        }`,
        alignItems: 'normal',
        height: 'auto',
        ...css,
      }}
    >
      {eyebrow && (
        <UtilityText as="p" size="eyebrow" css={{ marginBottom: '$quarter' }}>
          {eyebrow}
        </UtilityText>
      )}
      <StackLayout spacing="$threeQuarters" orientation="vertical">
        <StackLayout
          orientation="horizontal"
          verticalAlignment="center"
          spacing="$eighth"
        >
          {heading && (
            <HeadingText
              level={headingLevel || '3'}
              size="sm"
              css={{ paddingRight: '$quarter' }}
            >
              {heading}
            </HeadingText>
          )}
          {tooltipContent && (
            <Popover.Root>
              <Popover.Trigger
                size="small"
                priority="tertiary"
                label={tooltipLabel || ''}
                icon={icon}
                hideLabel
                quiet
                css={overrideGenesisFocusVisibleStyles}
              />
              <Popover.Content
                align="center"
                side="top"
                css={{ maxWidth: '360px' }}
                headingBar={
                  <Box
                    css={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '$quarter',
                      marginBlockEnd: '$quarter',
                    }}
                  >
                    {tooltipTitle && (
                      <HeadingText size="sm" level={tooltipHeadingLevel}>
                        {tooltipTitle}
                      </HeadingText>
                    )}
                    <Box
                      css={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: tooltipTitle
                          ? 'space-between'
                          : 'flex-end',
                        width: tooltipTitle ? undefined : '100%',
                      }}
                    >
                      <Popover.Close asChild>
                        <Button
                          type="button"
                          icon="tinyClose"
                          hideLabel
                          quiet
                          priority="secondary"
                          size="small"
                          className="GDS-popover-close"
                          css={{
                            justifySelf: 'flex-end',
                            color: '$interactiveActionSubdued',
                            svg: {
                              width: '$threeQuarters',
                              height: '$threeQuarters',
                            },
                            position: 'relative',
                            top: '-$quarter',
                            ...overrideGenesisFocusVisibleStyles,
                          }}
                        >
                          {formatMessage({ id: 'CLOSE' })}
                        </Button>
                      </Popover.Close>
                    </Box>
                  </Box>
                }
              >
                <StackLayout spacing="$two">
                  <ParagraphText size="sm">{tooltipContent}</ParagraphText>
                </StackLayout>
              </Popover.Content>
            </Popover.Root>
          )}
        </StackLayout>
        <ParagraphText size="sm">
          <ConditionalLinkRenderer
            disableDefaultOnClick
            ref={linkRefState}
            url={onClickDeeplinkAction?.payload.url}
            onClick={(event: React.MouseEvent) => {
              if (onClick) {
                invokeAction({
                  event,
                  ...onClick,
                });
              }
            }}
          >
            <UtilityText
              size="sm"
              emphasis={emphasizeCurrentValue ? 'emphasized' : 'base'}
            >
              {currentText}
            </UtilityText>{' '}
            {suffixText}
          </ConditionalLinkRenderer>
        </ParagraphText>
        <Box css={{ paddingBottom: '$half', width: '100%' }}>
          <ProgressBar
            fillProgressValue={progress}
            accessibilityLabel={`${currentText} ${suffixText}`}
          />
        </Box>
        <UtilityText
          emphasis={emphasizeFooterText ? 'emphasized' : 'subtle'}
          size="sm"
          css={{
            alignSelf: 'flex-end',
          }}
        >
          {footerText}
        </UtilityText>
      </StackLayout>
    </StackLayout>
  );
};
